// =============================================================================
// components/MobileWebGate.tsx — CRYB native-only strategy, WEB gate.
//
// CRYB is a native-first app (domain app.cryb.ai). ONE React Native codebase now
// also serves tablet + desktop BROWSERS via react-native-web. But phone-class
// browsers must NOT run the SPA — they get a clean "Download the CRYB app" screen
// with the App Store badge instead. Tablet + desktop web render the full app.
//
// CRITICAL: this gate NEVER affects native (iOS/Android). It only ever gates when
// Platform.OS === 'web' AND the browser userAgent is phone-class. On native the
// component renders its children unconditionally (isPhoneWeb() short-circuits to
// false because there is no phone-browser userAgent). It is wired at the web root
// (index.web.js) so it gates BEFORE the app mounts.
//
// Mirrors the pattern in apps/react-app/src/components/MobileWebGate.jsx (phone-
// class UA regex + App Store link) but built with RN primitives so it lives inside
// the one native codebase and reuses the app's theme tokens.
// =============================================================================
import React from 'react';
import {
  Platform,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Linking,
} from 'react-native';
import { colors, fonts, radius, spacing } from '../constants/theme';

// CRYB is iOS-only; there is no Google Play listing. (Matches apps/react-app.)
export const APP_STORE_URL = 'https://apps.apple.com/app/id6772768741';
export const PLATFORM_URL = 'https://app.cryb.ai';

/**
 * Phone-class WEB browser detection. TRUE only on web AND a phone userAgent —
 * tablets/iPad and desktop pass through, and native (no browser UA) is never
 * gated. Guarded so it is a hard `false` off the web target.
 */
export function isPhoneWeb(): boolean {
  if (Platform.OS !== 'web') return false;
  // `navigator` is a web-only global (not in the RN TS lib) — read it off
  // globalThis so this file type-checks without pulling in the DOM lib. Guarded by
  // the Platform.OS check above, so it is only ever touched on the web target.
  const nav = (globalThis as { navigator?: { userAgent?: string } }).navigator;
  if (!nav) return false;
  const ua = nav.userAgent || '';
  // Phone-class only. iPad reports as a desktop Mac on modern iPadOS and falls
  // through to the full app (intended — tablets get the app).
  return /iPhone|iPod|Android.*Mobile|Mobile.*Android|BlackBerry|IEMobile|Opera Mini|webOS/i.test(
    ua
  );
}

function GateScreen() {
  const openStore = React.useCallback(() => {
    Linking.openURL(APP_STORE_URL).catch(() => {});
  }, []);

  return (
    <View style={styles.root}>
      <View style={styles.card}>
        <Text style={styles.logo}>
          CRYB<Text style={styles.logoDot}>.</Text>
        </Text>
        <Text style={styles.title}>Get the app</Text>
        <Text style={styles.body}>
          CRYB isn&rsquo;t available in the mobile browser. Download the app for the
          full experience.
        </Text>

        <TouchableOpacity
          style={styles.cta}
          onPress={openStore}
          accessibilityRole="button"
          accessibilityLabel="Download CRYB on the App Store"
          activeOpacity={0.85}
        >
          <Text style={styles.ctaText}>Download on the App Store</Text>
        </TouchableOpacity>

        <Text style={styles.hint}>
          Or open {PLATFORM_URL.replace('https://', '')} on your computer.
        </Text>
      </View>
    </View>
  );
}

/**
 * Wrap the web root with this. On native, or on tablet/desktop web, it renders
 * children unchanged. Only a phone-class browser sees the download screen.
 */
export default function MobileWebGate({ children }: { children: React.ReactNode }) {
  const [blocked] = React.useState(() => isPhoneWeb());
  if (!blocked) return <>{children}</>;
  return <GateScreen />;
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xxl,
    // Brand lavender-glass world (constants/theme).
    backgroundColor: colors.bg,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 28,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    paddingVertical: 44,
    paddingHorizontal: 28,
    shadowColor: 'rgba(108,92,231,0.25)',
    shadowOffset: { width: 0, height: 24 },
    shadowOpacity: 1,
    shadowRadius: 48,
    elevation: 12,
  },
  logo: {
    fontSize: 42,
    fontWeight: '900',
    letterSpacing: -1.5,
    color: colors.primary,
    lineHeight: 44,
  },
  logoDot: { color: colors.primaryLight },
  title: {
    fontSize: 22,
    ...fonts.bold,
    color: colors.ink,
    marginTop: spacing.xl,
    marginBottom: spacing.sm,
  },
  body: {
    color: colors.ink2,
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 300,
    marginBottom: spacing.xxxl,
  },
  cta: {
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    paddingVertical: 14,
    paddingHorizontal: 28,
    alignSelf: 'stretch',
    alignItems: 'center',
  },
  ctaText: { color: colors.white, ...fonts.bold, fontSize: 16 },
  hint: {
    color: colors.muted,
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
    marginTop: spacing.xxl,
  },
});
